// typescript
// File: `typescript/apps/front-end/src/main/common/ApiUtils.ts`
import { useCallback, useState } from "react";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { toast } from "sonner";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { isNavigationInProgress } from "@/main/navigation/NavigationState";
import { LocalizationUtil } from "@/main/util/LocalizationUtil";
import logger from "@/lib/logger";

export async function unwrapAxiosData<T>(promise: Promise<any>): Promise<T> {
    const resp = await promise;
    return (resp && resp.data !== undefined) ? (resp.data as T) : (resp as T);
}

export function isInterruptedRequestError(error: unknown): boolean {
    if (isNavigationInProgress()) {
        return true;
    }

    if (!error || typeof error !== "object") {
        return false;
    }

    const maybeError = error as {
        code?: string;
        name?: string;
        message?: string;
        __CANCEL__?: boolean;
    };
    const normalizedMessage = typeof maybeError.message === "string"
        ? maybeError.message.trim().toLowerCase()
        : "";

    return maybeError.__CANCEL__ === true
        || maybeError.code === "ERR_CANCELED"
        || maybeError.name === "AbortError"
        || maybeError.name === "CanceledError"
        || normalizedMessage === "canceled"
        || normalizedMessage === "cancelled"
        || normalizedMessage === "aborted"
        // axios' XHR adapter rejects an aborted request (navigating away, unmounting, a crawler
        // bailing mid-render) with `new AxiosError('Request aborted', AxiosError.ECONNABORTED, …)`
        // — see node_modules/axios/lib/adapters/xhr.js. None of the checks above match that shape,
        // which is why aborted fetches were reaching prod as ERROR (2026-07-24 audit finding 11).
        //
        // Key on the MESSAGE, never the bare `ECONNABORTED` code: axios reuses that same code for
        // timeouts ("timeout of Nms exceeded", xhr.js:144) when `clarifyTimeoutError` is off, and a
        // timeout is a real failure that must keep surfacing.
        || normalizedMessage === "request aborted";
}

export type ValidationMap = Record<string, string[]>;

/**
 * Accept a map whose values can be arrays or strings (or other), and return the first readable message.
 */
export function getFirstValidationMessage(validationErrors: Record<string, unknown> | undefined | null): string | null {
    if (!validationErrors) return null;

    const anyMap = validationErrors as any;

    if (Array.isArray(anyMap.global) && anyMap.global.length > 0) {
        return anyMap.global.filter((x: any) => typeof x === "string").join(", ");
    }

    for (const k of Object.keys(validationErrors)) {
        const v = (validationErrors as any)[k];
        if (Array.isArray(v) && v.length > 0) {
            const strings = v.filter((x: any) => typeof x === "string");
            if (strings.length > 0) return strings.join(", ");
        }
        if (typeof v === "string" && v.trim().length > 0) {
            return v;
        }
    }
    return null;
}

/**
 * Attempt to extract a human friendly message from a server error payload.
 */
export function extractReadableMessageFromServer(obj: any): string | null {
    if (obj === null || obj === undefined) return null;
    if (typeof obj === "string") return obj;
    // validationErrors preferred
    if (obj.validationErrors && typeof obj.validationErrors === "object") {
        const m = getFirstValidationMessage(obj.validationErrors);
        if (m) return m;
    }
    // Common fields. Prefer the human-readable `message` over `error`: typed denials (401/403/409)
    // put a machine code in `error` (e.g. "FORBIDDEN", "UNAUTHORIZED") and the localized text in
    // `message`, so reading `error` first would surface the code instead of the reason.
    const candidates = [obj.message, obj.detail, obj.title, obj.error];
    for (const c of candidates) {
        if (typeof c === "string" && c.trim().length > 0) return c;
    }
    // some APIs put errors as a top-level errors map or array
    if (Array.isArray(obj.errors) && obj.errors.length > 0) {
        const filtered = obj.errors.filter((x: any) => typeof x === "string");
        if (filtered.length > 0) return filtered.join(", ");
    }
    if (typeof obj.errors === "object") {
        const m = getFirstValidationMessage(obj.errors);
        if (m) return m;
    }
    // fallback: look for any string value in object
    for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (typeof v === "string" && v.trim().length > 0) return v;
        if (Array.isArray(v) && v.length > 0 && typeof v[0] === "string") return v.join(", ");
    }
    return null;
}

function isSafeUserFacingErrorMessage(message: string): boolean {
    const normalizedMessage = message.trim();

    if (normalizedMessage.length === 0 || normalizedMessage.length > 220) {
        return false;
    }

    const lowerCaseMessage = normalizedMessage.toLowerCase();

    if (
        normalizedMessage.startsWith("{")
        || normalizedMessage.startsWith("[")
        || normalizedMessage.startsWith("<")
        || normalizedMessage.includes("\n")
        || normalizedMessage.includes("\r")
    ) {
        return false;
    }

    return !(
        lowerCaseMessage.includes("exception")
        || lowerCaseMessage.includes("traceback")
        || lowerCaseMessage.includes("stack trace")
        || lowerCaseMessage.includes("sqlstate")
        || lowerCaseMessage.includes("syntaxerror")
        || lowerCaseMessage.includes("axioserror")
        || lowerCaseMessage.includes("status code")
        || lowerCaseMessage.includes("network error")
        || lowerCaseMessage.includes("err_")
        || /\bat\s+.+\(.+\)/.test(normalizedMessage)
        || /\b(?:org|com|net|io)\.[\w.]+/.test(normalizedMessage)
    );
}

function getGenericApiErrorMessage(): string {
    try {
        return LocalizationUtil.get("error.unexpected");
    } catch {
        return "Something went wrong. Please try again.";
    }
}

function extractValidationErrorsFromResponse(resp: any): ValidationMap | null {
    if (!resp || typeof resp !== "object") {
        return null;
    }

    if (resp.validationErrors && typeof resp.validationErrors === "object") {
        return resp.validationErrors as ValidationMap;
    }

    if (resp.error === "VALIDATION") {
        const message =
            (typeof resp.message === "string" && resp.message.trim().length > 0 && resp.message) ||
            (typeof resp.detail === "string" && resp.detail.trim().length > 0 && resp.detail) ||
            (typeof resp.title === "string" && resp.title.trim().length > 0 && resp.title) ||
            getGenericApiErrorMessage();

        return { global: [message] };
    }

    return null;
}

/**
 * Coerce a single field→value(s) map (e.g. a server `validationErrors`/`errors` object)
 * into the canonical `Record<string, string[]>` shape, stringifying scalars, arrays, and
 * nested objects defensively. Unknown/object values fall back to their stringified values.
 */
function coerceFieldErrorsToArrays(source: any): ValidationMap {
    const out: ValidationMap = {};
    if (!source || typeof source !== "object") {
        return out;
    }
    for (const [key, value] of Object.entries(source)) {
        if (Array.isArray(value)) {
            out[key] = value.map((item) => String(item));
        } else if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            out[key] = [String(value)];
        } else if (value && typeof value === "object") {
            try {
                const values = Array.isArray(value) ? value : Object.values(value);
                out[key] = values.map((item: any) => String(item));
            } catch {
                out[key] = [String(value)];
            }
        } else {
            out[key] = [String(value)];
        }
    }
    return out;
}

export type NormalizeApiValidationErrorsOptions = {
    /**
     * When the payload is a bare field map (no `validationErrors`/`errors` envelope),
     * restrict the recognized keys to this allow-list. Omit to accept any envelope only.
     */
    allowedFields?: ReadonlySet<string>;
    /**
     * When no field errors were extracted but the payload carries a top-level `message`,
     * surface that message under a single field. The field is `validationField` when the
     * payload is a typed `VALIDATION` error, otherwise `defaultField`.
     */
    messageFallback?: {
        defaultField: string;
        validationField: string;
    };
};

/**
 * Shared normalizer for the several near-identical "coerce a server error payload into
 * `Record<string, string[]>`" routines that previously lived inline in the discussion and
 * argument editors. It understands the common `validationErrors` / `errors` envelopes, an
 * optional allow-listed bare field map, and an optional top-level `message` fallback.
 */
export function normalizeApiValidationErrors(
    data: any,
    options: NormalizeApiValidationErrorsOptions = {},
): ValidationMap {
    if (!data) {
        return {};
    }

    if (data.validationErrors && typeof data.validationErrors === "object") {
        return coerceFieldErrorsToArrays(data.validationErrors);
    }
    if (data.errors && typeof data.errors === "object") {
        return coerceFieldErrorsToArrays(data.errors);
    }

    const out: ValidationMap = {};
    if (typeof data === "object") {
        if (options.allowedFields) {
            const fieldEntries = Object.entries(data)
                .filter(([key]) => options.allowedFields!.has(key));
            if (fieldEntries.length > 0) {
                Object.assign(out, coerceFieldErrorsToArrays(Object.fromEntries(fieldEntries)));
            }
        }

        if (
            options.messageFallback
            && Object.keys(out).length === 0
            && typeof data.message === "string"
            && data.message.trim().length > 0
        ) {
            const field = data.error === "VALIDATION"
                ? options.messageFallback.validationField
                : options.messageFallback.defaultField;
            out[field] = [data.message.trim()];
        }
    }
    return out;
}

function getSafeApiToastMessage(resp: any): string {
    const extractedMessage = extractReadableMessageFromServer(resp);

    if (!extractedMessage || !isSafeUserFacingErrorMessage(extractedMessage)) {
        return getGenericApiErrorMessage();
    }

    return extractedMessage;
}

/**
 * Extract a safe, user-facing message from a thrown request error (axios or otherwise),
 * falling back to the caller-provided localized message when the server text is missing
 * or unsafe to display (technical strings, stack traces, JSON/HTML blobs, etc.).
 *
 * Prefer this over reading `error.response.data.message` directly so technical/non-localized
 * server text never leaks into a toast.
 */
export function getSafeToastMessageFromError(error: unknown, fallback: string): string {
    const resp = (error as any)?.response?.data ?? (error as any)?.data ?? null;
    const extractedMessage = extractReadableMessageFromServer(resp);

    if (!extractedMessage || !isSafeUserFacingErrorMessage(extractedMessage)) {
        return fallback;
    }

    return extractedMessage;
}

/**
 * Whether this rejection is one the server MEANT to send and the user is being told about.
 *
 * <p>A 4xx carrying a readable message that we surface in a toast is a designed outcome, not a
 * failure: "you cannot review a post only your groups can see" is the feature working. Logging it at
 * `error` forwards it to `/api/client-logs`, which is the same channel an island CRASH reports
 * through — so every author who hits a designed refusal adds noise to the one signal that is supposed
 * to mean the UI broke. It also makes the refusal untestable: the Playwright island-health fixture is
 * `auto` across every lane and fails any spec that provokes a forwarded frontend error.
 *
 * <p>401 is deliberately NOT included — the session handler owns that case and returns earlier — and
 * 5xx never is: a server fault is a real error even when the user sees a polite message.
 *
 * <p>Use this to choose a LOG LEVEL. Never to decide what to show the user: the toast text still
 * comes from `getSafeToastMessageFromError`.
 */
export function isExpectedUserFacingRejection(error: unknown): boolean {
    const status = (error as any)?.response?.status ?? (error as any)?.status;
    if (typeof status !== "number" || status < 400 || status >= 500 || status === 401) {
        return false;
    }
    const data = (error as any)?.response?.data ?? (error as any)?.data ?? null;
    const message = extractReadableMessageFromServer(data);
    return Boolean(message && isSafeUserFacingErrorMessage(message));
}

/**
 * Log a rejected API call at the level it deserves: `warn` for a refusal the server MEANT to send,
 * `error` for anything else.
 *
 * <p>Promoted out of `ReviewFacetDialog` on 2026-08-12 (front-end H0-A), which was the only adopter
 * `isExpectedUserFacingRejection` ever had. The argument order matches the `logger.error(message, error)`
 * calls it replaces so the swap stays a one-token diff, and every extra argument is forwarded
 * untouched — this changes the LEVEL and nothing else, so a 4xx refusal is still recorded in full.
 *
 * <p>Why it matters: `logger.error` forwards to `/api/client-logs`, the same channel a crashed island
 * reports itself through, and the Playwright island-health fixture is `auto` across every lane. So a
 * designed refusal logged at `error` both dilutes the one signal that means the UI broke AND makes the
 * refusal untestable — any spec provoking a legitimate permission denial fails its lane.
 *
 * <p>Use it wherever a rejected request already has a user-visible outcome (a toast, an inline banner,
 * a list error state, or a re-throw to a wrapper that renders one). Do NOT use it to decide what to
 * show the user — that text still comes from {@link getSafeToastMessageFromError}.
 */
export function logApiFailure(message: string, error: unknown, ...extra: unknown[]): void {
    if (isExpectedUserFacingRejection(error)) {
        logger.warn(message, error, ...extra);
        return;
    }
    logger.error(message, error, ...extra);
}

/**
 * Reads the optional stable machine `code` a typed backend rejection carries next to its localized
 * `message` (`AjaxBaseRestController.handleValidationException`, codes in `ValidationErrorCodes`).
 *
 * Use it to *act* on one specific rejection — never to build user-facing text. Absent on the ~all
 * rejections that stay generic, so callers must handle `null`.
 */
export function getServerErrorCode(error: unknown): string | null {
    const data = (error as any)?.response?.data ?? (error as any)?.data ?? null;
    const code = data && typeof data === "object" ? (data as any).code : null;
    return typeof code === "string" && code.trim().length > 0 ? code : null;
}

export function getValidationErrorsFromError(
    error: any,
    fallbackValidationErrors?: ValidationMap | null
): ValidationMap | null {
    if (fallbackValidationErrors && Object.keys(fallbackValidationErrors).length > 0) {
        return fallbackValidationErrors;
    }

    const resp = error?.response?.data ?? error?.data ?? null;
    return extractValidationErrorsFromResponse(resp);
}

export function applyValidationErrorsToForm<TFieldValues extends FieldValues>(
    setError: UseFormSetError<TFieldValues>,
    validationErrors: Record<string, unknown> | null | undefined
) {
    if (!validationErrors || typeof validationErrors !== "object") {
        return;
    }

    for (const key of Object.keys(validationErrors)) {
        if (key === "global") continue;

        const value = validationErrors[key];

        if (Array.isArray(value)) {
            const messages = value.filter(
                (item): item is string => typeof item === "string" && item.trim().length > 0
            );

            if (messages.length > 0) {
                setError(key as Path<TFieldValues>, { type: "server", message: messages.join(", ") });
            }
            continue;
        }

        if (typeof value === "string" && value.trim().length > 0) {
            setError(key as Path<TFieldValues>, { type: "server", message: value });
        }
    }
}

type UseApiFormResult<TSuccess> = {
    submitting: boolean;
    validationErrors: ValidationMap;
    successData: TSuccess | null;
    submit: (payload: any) => Promise<TSuccess>;
    clearErrors: () => void;
    resetSuccess: () => void;
};

export function useApiForm<TSuccess = any>(
    submitFn: (payload: any) => Promise<any>
): UseApiFormResult<TSuccess> {
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationMap>({});
    const [successData, setSuccessData] = useState<TSuccess | null>(null);

    const clearErrors = useCallback(() => {
        setValidationErrors({});
    }, []);

    // Drop a prior success so the form can return to its input state — e.g. letting a signup whose
    // verification email went to a mistyped address start over with a different one.
    const resetSuccess = useCallback(() => {
        setSuccessData(null);
    }, []);

    const submit = useCallback(async (payload: any): Promise<TSuccess> => {
        setSubmitting(true);
        clearErrors();
        try {
            const data = await unwrapAxiosData<TSuccess>(submitFn(payload));
            setSuccessData(data ?? null);
            return data as TSuccess;
        } catch (e: any) {
            const resp = e?.response?.data ?? e?.data ?? null;
            const validationErrorsFromResponse = extractValidationErrorsFromResponse(resp);

            if (validationErrorsFromResponse) {
                setValidationErrors(validationErrorsFromResponse);
            } else if (!isHandledAuthSessionError(e)) {
                toast.error(getSafeApiToastMessage(resp));
            }
            throw e;
        } finally {
            setSubmitting(false);
        }
    }, [clearErrors, submitFn]);

    return { submitting, validationErrors, successData, submit, clearErrors, resetSuccess };
}

/**
 * Run the provided submit function with the given payload and:
 * - return `{ data }` when successful
 * - return `{ validationErrors }` when server responds with validationErrors (no toast)
 * - toast and rethrow for other/general errors
 *
 * Usage:
 *   const res = await submitWithValidation(apiFn, payload);
 *   if (res.validationErrors) { ...apply to form... }
 *   else { /* success in res.data *\/ }
 */
export async function submitWithValidation<TSuccess >(
    submitFn: (payload: any) => Promise<TSuccess>,
    payload: any
): Promise<{ data?: TSuccess; validationErrors?: ValidationMap }> {
    try {
        const data = await unwrapAxiosData<TSuccess>(submitFn(payload));
        return { data };
    } catch (e: any) {
        const resp = e?.response?.data ?? e?.data ?? null;
        const validationErrors = extractValidationErrorsFromResponse(resp);
        if (validationErrors) {
            return { validationErrors };
        }
        if (isHandledAuthSessionError(e)) {
            throw e;
        }
        // general error: toast and rethrow so callers can abort/cleanup
        toast.error(getSafeApiToastMessage(resp));
        throw e;
    }
}
